/**
 * Recurring templates: the bills the household knows are coming (Phase 1, step 1.5).
 *
 * A template never posts by itself (B1) — there is no scheduler, and a number nobody looked
 * at is exactly the kind of figure this system refuses to invent. It produces a forecast; a
 * person taps Xác nhận and only then does an entry exist.
 *
 * Both the forecast and the due-date arithmetic live in SQL (`app.upcoming_obligations`,
 * `app.next_due_date`): `app.confirm_recurring` has to advance the stored date anyway, and a
 * second copy of that rule in TypeScript is a rule that will drift.
 */
import type { EntryType, PlanningNature } from '@pfo/domain';
import type { Json } from '@pfo/database';

import { supabase } from '../../../shared/api/supabase/client.js';
import { appErrorException } from '../../../shared/lib/errors.js';

export type RecurringFrequency = 'weekly' | 'monthly' | 'quarterly' | 'yearly';
export type AmountMode = 'fixed' | 'estimated';

/** One occurrence the household owes, as `app.upcoming_obligations` reports it. */
export type Obligation = Readonly<{
  templateId: string;
  name: string;
  entryType: string;
  amountMode: AmountMode;
  /** YYYY-MM-DD — the day the household owes it, not an instant. */
  dueDate: string;
  /** null for an estimated template: the bill has not arrived yet. */
  amount: number | null;
  isOverdue: boolean;
}>;

export type RecurringTemplateLine = Readonly<{
  ledgerAccountId: string;
  side: 'debit' | 'credit';
  amount: number | null;
  categoryId: string | null;
  planningNature: PlanningNature | null;
  sortOrder: number;
}>;

export type RecurringTemplate = Readonly<{
  id: string;
  name: string;
  entryType: EntryType;
  amountMode: AmountMode;
  frequency: RecurringFrequency;
  dayOfMonth: number | null;
  startDate: string;
  endDate: string | null;
  nextDueDate: string;
  isActive: boolean;
  lines: readonly RecurringTemplateLine[];
  /** The last correction recorded on the template (AE2): what it was and for which occurrence. */
  lastOccurrence: Readonly<{ action: string; dueDate: string }> | null;
}>;

/** YYYY-MM-DD of "now" in the household's timezone — the day the family is living. */
export function todayInTimeZone(timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export async function fetchUpcomingObligations(
  from: string,
  days: number,
): Promise<readonly Obligation[]> {
  const { data, error } = await supabase
    .schema('app')
    .rpc('upcoming_obligations', { p_from: from, p_days: days });
  if (error) throw appErrorException(error);

  return (data ?? []).map((row) => ({
    templateId: row.template_id ?? '',
    name: row.name ?? '',
    entryType: row.entry_type ?? 'expense',
    amountMode: (row.amount_mode ?? 'fixed') as AmountMode,
    dueDate: row.due_date ?? '',
    amount: row.amount === null ? null : Number(row.amount),
    isOverdue: row.is_overdue ?? false,
  }));
}

/**
 * Turns one occurrence into a real entry. `amount` is required when the template is
 * estimated — the RPC refuses it otherwise (`AMOUNT_REQUIRED`) rather than guess the bill.
 */
export async function confirmRecurring(input: {
  templateId: string;
  dueDate: string;
  amount?: number;
  note?: string;
}): Promise<string> {
  const overrides: Record<string, unknown> = {};
  if (input.amount !== undefined) overrides['amount'] = input.amount;
  if (input.note !== undefined && input.note !== '') overrides['note'] = input.note;

  const { data, error } = await supabase.schema('app').rpc('confirm_recurring', {
    p_template_id: input.templateId,
    p_due_date: input.dueDate,
    p_overrides: overrides as Json,
  });
  if (error) throw appErrorException(error);
  return data;
}

export async function fetchTemplates(): Promise<readonly RecurringTemplate[]> {
  const [templatesResult, linesResult] = await Promise.all([
    supabase
      .from('recurring_templates')
      // One literal, deliberately long: PostgREST derives the row type from the string, and
      // splitting it with `+` throws that away.
      // prettier-ignore
      .select(
        'id, name, entry_type, amount_mode, frequency, day_of_month, start_date, end_date, next_due_date, is_active, metadata',
      )
      .order('is_active', { ascending: false })
      .order('next_due_date'),
    supabase
      .from('recurring_template_lines')
      // prettier-ignore
      .select(
        'template_id, ledger_account_id, side, amount, category_id, planning_nature, sort_order',
      )
      .order('sort_order'),
  ]);

  if (templatesResult.error) throw appErrorException(templatesResult.error);
  if (linesResult.error) throw appErrorException(linesResult.error);

  const linesByTemplate = new Map<string, RecurringTemplateLine[]>();
  for (const row of linesResult.data ?? []) {
    const list = linesByTemplate.get(row.template_id) ?? [];
    list.push({
      ledgerAccountId: row.ledger_account_id,
      side: row.side as 'debit' | 'credit',
      amount: row.amount === null ? null : Number(row.amount),
      categoryId: row.category_id,
      planningNature: row.planning_nature as PlanningNature | null,
      sortOrder: row.sort_order,
    });
    linesByTemplate.set(row.template_id, list);
  }

  return (templatesResult.data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    entryType: row.entry_type as EntryType,
    amountMode: row.amount_mode as AmountMode,
    frequency: row.frequency as RecurringFrequency,
    dayOfMonth: row.day_of_month,
    startDate: row.start_date,
    endDate: row.end_date,
    nextDueDate: row.next_due_date,
    isActive: row.is_active,
    lastOccurrence: lastOccurrenceOf(row.metadata),
    lines: linesByTemplate.get(row.id) ?? [],
  }));
}

export type NewTemplate = Readonly<{
  householdId: string;
  subjectId: string;
  name: string;
  entryType: EntryType;
  amountMode: AmountMode;
  frequency: RecurringFrequency;
  dayOfMonth: number | null;
  startDate: string;
  /** The first occurrence. Everything after it is computed by `app.next_due_date`. */
  nextDueDate: string;
  lines: readonly RecurringTemplateLine[];
}>;

export async function createTemplate(input: NewTemplate): Promise<string> {
  const { data, error } = await supabase
    .from('recurring_templates')
    .insert({
      household_id: input.householdId,
      subject_id: input.subjectId,
      name: input.name,
      entry_type: input.entryType,
      amount_mode: input.amountMode,
      frequency: input.frequency,
      day_of_month: input.dayOfMonth,
      start_date: input.startDate,
      next_due_date: input.nextDueDate,
    })
    .select('id')
    .single();
  if (error) throw appErrorException(error);

  const templateId = data.id;
  const { error: linesError } = await supabase.from('recurring_template_lines').insert(
    input.lines.map((line, index) => ({
      household_id: input.householdId,
      template_id: templateId,
      ledger_account_id: line.ledgerAccountId,
      side: line.side,
      amount: line.amount,
      category_id: line.categoryId,
      planning_nature: line.planningNature,
      sort_order: line.sortOrder === 0 ? index : line.sortOrder,
    })),
  );
  if (linesError) throw appErrorException(linesError);

  return templateId;
}

/**
 * "Lưu thành định kỳ": turns an entry already on the books into a template, lines and all.
 *
 * The copy happens in SQL (`app.template_from_entry`) rather than by rebuilding the lines
 * from form fields — that is what lets a five-way salary split become a five-line template
 * without anyone re-entering it (N9, deferred here from step 1.3).
 */
export async function createTemplateFromEntry(input: {
  entryId: string;
  name: string;
  frequency: RecurringFrequency;
  dayOfMonth: number | null;
  amountMode: AmountMode;
  firstDue: string;
}): Promise<string> {
  const { data, error } = await supabase.schema('app').rpc('template_from_entry', {
    p_entry_id: input.entryId,
    p_name: input.name,
    p_frequency: input.frequency,
    // `int` is nullable in Postgres — a weekly template has no day of the month — but the
    // generated Args type spells every argument non-null, so the null is asserted here.
    p_day_of_month: input.dayOfMonth as number,
    p_amount_mode: input.amountMode,
    p_first_due: input.firstDue,
  });
  if (error) throw appErrorException(error);
  return data;
}

/** Retired, not deleted: entries already posted keep pointing at the template that made them. */
export async function setTemplateActive(templateId: string, isActive: boolean): Promise<void> {
  const { error } = await supabase
    .from('recurring_templates')
    .update({ is_active: isActive, updated_at: new Date().toISOString() })
    .eq('id', templateId);
  if (error) throw appErrorException(error);
}

/** One occurrence of a template as `app.recurring_template_history` reports it (AJ3). */
export type TemplateOccurrence = Readonly<{
  dueDate: string;
  status: 'posted' | 'voided' | 'skipped' | 'pending' | 'overdue';
  entryId: string | null;
  amount: number | null;
  occurredAt: string | null;
  markedAt: string | null;
  reason: string | null;
}>;

/**
 * Every occurrence of one template between two dates, newest first — the body of the detail
 * screen. State is decided in SQL from the ledger and the audit trail; this only formats.
 */
export async function fetchTemplateHistory(
  templateId: string,
  from: string,
  to: string,
): Promise<readonly TemplateOccurrence[]> {
  const { data, error } = await supabase
    .schema('app')
    .rpc('recurring_template_history', { p_template_id: templateId, p_from: from, p_to: to });
  if (error) throw appErrorException(error);
  return (data ?? []).map((row) => ({
    dueDate: row.due_date ?? '',
    status: (row.status ?? 'pending') as TemplateOccurrence['status'],
    entryId: row.entry_id ?? null,
    amount: row.amount === null || row.amount === undefined ? null : Number(row.amount),
    occurredAt: row.occurred_at ?? null,
    markedAt: row.marked_at ?? null,
    reason: row.reason ?? null,
  }));
}

/**
 * Undoes one confirmation (plan R1, decision AE1).
 *
 * `skip` — the period is handled some other way (the money was already in the opening
 * balance): the entry is voided and the schedule stays where it is.
 * `redo` — the entry itself was wrong: the occurrence comes back on Đến hạn to be confirmed
 * again. Refused with `OUT_OF_ORDER` when a later period is already on the books.
 */
export async function undoRecurringConfirm(input: {
  entryId: string;
  mode: 'skip' | 'redo';
  reason?: string;
}): Promise<void> {
  const { error } = await supabase.schema('app').rpc('undo_recurring_confirm', {
    p_entry_id: input.entryId,
    p_mode: input.mode,
    ...(input.reason === undefined ? {} : { p_reason: input.reason }),
  });
  if (error) throw appErrorException(error);
}

/** `metadata.occurrences[]` is the audit trail AE2 writes; its last element is the latest correction. */
function lastOccurrenceOf(metadata: Json | null): RecurringTemplate['lastOccurrence'] {
  if (metadata === null || typeof metadata !== 'object' || Array.isArray(metadata)) return null;
  const occurrences = (metadata as Record<string, Json | undefined>)['occurrences'];
  if (!Array.isArray(occurrences) || occurrences.length === 0) return null;
  const last = occurrences[occurrences.length - 1];
  if (last === null || typeof last !== 'object' || Array.isArray(last)) return null;
  const record = last as Record<string, Json | undefined>;
  return typeof record['action'] === 'string' && typeof record['due_date'] === 'string'
    ? { action: record['action'], dueDate: record['due_date'] }
    : null;
}

/**
 * Takes back the skip that happened last, so the occurrence comes due again (decision AJ1).
 * The real case: a salary occurrence skipped because the money was already in the opening
 * balance — once that balance is corrected, the salary has to be recorded after all.
 */
export async function restoreRecurring(input: {
  templateId: string;
  dueDate: string;
  reason?: string;
}): Promise<string> {
  const { data, error } = await supabase.schema('app').rpc('restore_recurring', {
    p_template_id: input.templateId,
    p_due_date: input.dueDate,
    ...(input.reason === undefined ? {} : { p_reason: input.reason }),
  });
  if (error) throw appErrorException(error);
  return data;
}

/** Moves a template past one occurrence without posting anything (plan R2). */
export async function skipRecurring(input: {
  templateId: string;
  dueDate: string;
  reason?: string;
}): Promise<string> {
  const { data, error } = await supabase.schema('app').rpc('skip_recurring', {
    p_template_id: input.templateId,
    p_due_date: input.dueDate,
    ...(input.reason === undefined ? {} : { p_reason: input.reason }),
  });
  if (error) throw appErrorException(error);
  return data;
}

/**
 * Edits a template (plan R3, decision AE3): scalar fields plus a wholesale replacement of its
 * lines. Takes effect from the next unconfirmed occurrence; entries already posted never move.
 */
export async function replaceRecurringTemplate(input: {
  templateId: string;
  patch: {
    name?: string;
    amountMode?: AmountMode;
    frequency?: RecurringFrequency;
    dayOfMonth?: number | null;
    endDate?: string | null;
    nextDueDate?: string;
  };
  lines: readonly RecurringTemplateLine[] | null;
}): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (input.patch.name !== undefined) patch['name'] = input.patch.name;
  if (input.patch.amountMode !== undefined) patch['amount_mode'] = input.patch.amountMode;
  if (input.patch.frequency !== undefined) patch['frequency'] = input.patch.frequency;
  if (input.patch.dayOfMonth !== undefined) patch['day_of_month'] = input.patch.dayOfMonth;
  if (input.patch.endDate !== undefined) patch['end_date'] = input.patch.endDate;
  if (input.patch.nextDueDate !== undefined) patch['next_due_date'] = input.patch.nextDueDate;

  const { error } = await supabase.schema('app').rpc('replace_recurring_template', {
    p_template_id: input.templateId,
    p_patch: patch as Json,
    p_lines:
      input.lines === null
        ? null
        : (input.lines.map((line, index) => ({
            ledger_account_id: line.ledgerAccountId,
            side: line.side,
            amount: line.amount,
            category_id: line.categoryId,
            planning_nature: line.planningNature,
            sort_order: line.sortOrder === 0 ? index : line.sortOrder,
          })) as unknown as Json),
  });
  if (error) throw appErrorException(error);
}
